<template>
  <div v-if="visible" class="custom-modal-overlay">

    <div v-if="isRegisterProcessing" class="processing-overlay">
      <div class="processing-content">
        <el-icon class="processing-icon" :size="32"><Loading /></el-icon>
        <p class="processing-text">{{ registerProcessingText }}</p>
      </div>
    </div>

    <div class="custom-modal register-modal" @click.stop>
      <div class="modal-body">
        <div class="modal-form-wrapper">
          <!-- 关闭按钮 -->
          <el-icon class="close-btn" @click="handleClose">
            <Close />
          </el-icon>

          <!-- 顶部注册提示 -->
          <div class="modal-header">
            <h3>微信扫码注册</h3>
          </div>

          <!-- 微信注册容器 -->
          <div class="wechat-register-container">
            <div v-if="wechatQrCodeUrl" class="wechat-qrcode">
              <img :src="wechatQrCodeUrl" alt="微信注册二维码">
              <p class="tip">请使用微信扫一扫注册</p>
            </div>
            <div v-else-if="wechatQrError" class="qrcode-error">
              <p>{{ wechatQrError }}</p>
              <el-button text type="primary" @click="generateWechatQrCode">重新加载</el-button>
            </div>
            <div v-else class="loading-qrcode">
              正在生成二维码...
            </div>
          </div>

          <!-- 登录链接 -->
          <div class="login-link">
            <span>已有账号？</span>
            <el-link type="primary" @click="switchToLogin">去登录</el-link>
          </div>

          <!-- 用户协议和隐私政策 -->
          <div class="agreement">
            注册即表示同意
            <el-link type="primary" @click="showTermsDialog">《用户协议》</el-link>
            和
            <el-link type="primary" @click="showPrivacyDialog">《隐私政策》</el-link>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 绑定手机弹窗 -->
  <BindPhoneDialog
      v-model="bindPhoneDialogVisible"
      :session-id="currentSessionId"
      @bind-success="handleBindSuccess"
  />

  <!-- 服务条款弹窗 -->
  <el-dialog
      v-model="termsDialogVisible"
      :title="null"
      width="60%"
      top="8vh"
      class="policy-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
  >
    <template #header>
      <div class="dialog-header">
        <h3>用户协议</h3>
      </div>
    </template>
    <div class="dialog-content" v-html="termsContent"></div>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="termsDialogVisible = false">关闭</el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 隐私政策弹窗 -->
  <el-dialog
      v-model="privacyDialogVisible"
      :title="null"
      width="60%"
      top="8vh"
      class="policy-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
  >
    <template #header>
      <div class="dialog-header">
        <h3>隐私政策</h3>
      </div>
    </template>
    <div class="dialog-content" v-html="privacyContent"></div>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="privacyDialogVisible = false">关闭</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Close } from '@element-plus/icons-vue'
import request from '@/utils/request'
import BindPhoneDialog from '@/components/BindPhoneDialog.vue'
import { useAuthState } from '@/stores/authState'
import { TERMS_CONTENT, PRIVACY_CONTENT } from '@/constants/authContent'

const { setToken } = useAuthState()

// 属性定义
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  paymentInfo: {
    type: Object,
    default: () => ({})
  }
})

// 事件定义
const emit = defineEmits(['update:modelValue', 'register-success', 'switch-to-login'])

// 响应式数据
const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// 添加注册成功加载状态
const isRegisterProcessing = ref(false)
const registerProcessingText = ref('')

// 微信注册相关
const wechatQrCodeUrl = ref('')
const wechatQrError = ref('')
const currentSessionId = ref('')
const bindPhoneDialogVisible = ref(false)
let pollingTimer = null

// 弹窗控制变量
const termsDialogVisible = ref(false)
const privacyDialogVisible = ref(false)

// 用户协议内容
const termsContent = ref(TERMS_CONTENT)

// 隐私政策内容
const privacyContent = ref(PRIVACY_CONTENT)

// 方法
const handleClose = () => {
  isRegisterProcessing.value = false
  registerProcessingText.value = ''
  visible.value = false
  resetForm()
}

const switchToLogin = () => {
  emit('switch-to-login')
}

// 清理轮询函数
const clearPolling = () => {
  if (pollingTimer) {
    clearTimeout(pollingTimer)
    pollingTimer = null
  }
}

const resetForm = () => {
  wechatQrCodeUrl.value = ''
  wechatQrError.value = ''
  currentSessionId.value = ''
  clearPolling()

  isRegisterProcessing.value = false
  registerProcessingText.value = ''
}

// 生成微信注册二维码
const generateWechatQrCode = async () => {
  wechatQrError.value = ''
  try {
    const response = await request.get('/auth/wechat/mp-qrcode')
    const { qrUrl, sessionId } = response.data

    wechatQrCodeUrl.value = qrUrl
    currentSessionId.value = sessionId

    // 启动轮询检查注册状态
    startPolling()
  } catch (error) {
    const raw = error?.response?.data?.message || error?.message || ''
    wechatQrError.value = /AccessToken|appid|微信服务号/i.test(raw)
      ? '测试环境暂未配置微信服务号，请使用账号注册'
      : (raw || '微信注册暂不可用，请使用账号注册')
  }
}

// 启动轮询
const startPolling = () => {
  clearPolling()

  const poll = async () => {
    try {
      const statusRes = await request.get('/auth/wechat/login/status', {
        params: { sessionId: currentSessionId.value }
      })

      const responseData = statusRes.data || {}

      if (responseData.status === 'success') {
        const token = responseData.token
        const hasBoundPhone = responseData.hasBoundPhone

        // 保存token到localStorage
setToken( token)
        console.log('模态框注册Token存储完成:', localStorage.getItem('auth_token'));

        // 显示处理状态
        isRegisterProcessing.value = true
        registerProcessingText.value = '注册成功，正在处理...'


        // 等待用户信息加载完成
        await new Promise(resolve => setTimeout(resolve, 600));

        // 关闭处理状态
        isRegisterProcessing.value = false
        registerProcessingText.value = ''

        if (hasBoundPhone) {
          ElMessage.success('微信注册成功')
          clearPolling()
          handleClose()
          emit('register-success', {
            token,
            hasBoundPhone,
            paymentInfo: props.paymentInfo
          })
        } else {
          // 显示绑定手机弹窗
          bindPhoneDialogVisible.value = true
        }
      } else if (responseData.status === 'waiting_bind') {
        // 等待绑定手机的状态，显示绑定弹窗
        bindPhoneDialogVisible.value = true
      } else if (responseData.status === 'expired') {
        ElMessage.warning('二维码已过期，正在重新生成...')
        generateWechatQrCode()
      } else if (responseData.status === 'waiting') {
        pollingTimer = setTimeout(poll, 2000)
      }
    } catch (error) {
      console.error('轮询错误:', error)
      pollingTimer = setTimeout(poll, 2000)
    }
  }

  pollingTimer = setTimeout(poll, 2000)
}

// 处理绑定成功
const handleBindSuccess = () => {
  // 显示处理状态
  isRegisterProcessing.value = true
  registerProcessingText.value = '绑定成功，正在处理...'

  clearPolling()


  // 等待用户信息加载完成后再关闭
  setTimeout(() => {
    isRegisterProcessing.value = false
    registerProcessingText.value = ''

    ElMessage.success('注册成功！')
    handleClose()
    emit('register-success', {
      paymentInfo: props.paymentInfo
    })
  }, 600);
}

// 显示对话框的方法
const showTermsDialog = () => {
  termsDialogVisible.value = true
}

const showPrivacyDialog = () => {
  privacyDialogVisible.value = true
}

watch(visible, (newVal) => {
  if (newVal) {
    // 显示时锁定滚动
    document.body.style.overflow = 'hidden'
    // 生成二维码
    generateWechatQrCode()
  } else {
    // 隐藏时恢复滚动
    document.body.style.overflow = ''
    resetForm()
  }
})

</script>

<style scoped lang="scss">
/* 添加处理中覆盖层样式 */
.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  border-radius: 8px;
}

.processing-content {
  text-align: center;
}

.processing-icon {
  color: #409EFF;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

.processing-text {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 自定义模态框样式 */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.custom-modal {
  width: 400px;
  height: 500px;
  background: white;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-body {
  display: flex;
  height: 500px;
}

.modal-image {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 1px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.modal-form-wrapper {
  flex: 1;
  padding: 40px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 18px;
  cursor: pointer;
  color: #999;
  z-index: 10;

  &:hover {
    color: #409EFF;
  }
}

.modal-header {
  margin-top: -30px;
  margin-bottom: 25px;
  text-align: center;

  h3 {
    font-size: 24px;
    color: #2c3e50;
    margin-bottom: 8px;
    font-weight: 600;
  }
}

.modal-form {
  flex: 1;
  display: flex;
  flex-direction: column;

  :deep(.el-form-item) {
    margin-bottom: 10px;
  }
}

.modal-elegant-input {
  :deep(.el-input__wrapper) {
    border-radius: 0;
    padding: 1px 12px;
    box-shadow: 0 0 0 1px #dcdfe6 inset;
    height: 38px;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 0 0 1px #c0c4cc inset;
    }

    &.is-focus {
      box-shadow: 0 0 0 1px #409EFF inset;
    }
  }

  :deep(.el-input__inner) {
    line-height: 36px;
    font-size: 14px;
    color: #606266;
  }

  :deep(.el-input__prefix) {
    left: 8px;
    color: #606266;
  }

  :deep(.el-input__suffix) {
    display: flex;
    align-items: center;
    padding-right: 8px;
  }

  .captcha-btn {
    padding: 0;
    height: auto;
    color: #373737;
    font-size: 13px;
    white-space: nowrap;

    &:hover {
      color: #66b1ff;
    }

    &:disabled {
      color: #c0c4cc;
      cursor: not-allowed;
    }
  }
}

.register-btn {
  width: 100%;
  margin-top: 8px;
  border-radius: 0;
  padding: 10px 16px;
  font-weight: 600;
  letter-spacing: 6px;
  font-size: 14px;
  height: 42px;
}

.login-link {
  text-align: center;
  font-size: 14px;
  padding-bottom: 15px;
  margin-top: 5px;
  border-bottom: 2px solid #d9d9d9;

  span {
    color: #606266;
    vertical-align: middle;
  }

  .el-link {
    font-size: 14px;
    vertical-align: middle;
  }
}

.agreement {
  text-align: center;
  font-size: 12px;
  color: #909399;
  margin-top: 10px;
  line-height: 1.6;

  .agreement-content {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;

    .agree-checkbox {
      margin-right: 4px;

      // 防止点击复选框标签时触发链接
      :deep(.el-checkbox__label) {
        cursor: default;
        user-select: none;
      }
    }

    .agreement-links {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .el-link {
      font-size: 12px;
      vertical-align: baseline;

      &:hover {
        color: #409EFF;
      }
    }

    span {
      color: #909399;
      font-size: 12px;
    }
  }
}

.error-message {
  display: flex;
  align-items: center;
  color: #f84949;
  font-size: 12px;
  margin-top: 4px;
  height: 20px;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

.success-message {
  display: flex;
  align-items: center;
  color: #67c23a;
  font-size: 12px;
  margin-top: 4px;
  height: 20px;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

.wechat-register-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;

  .wechat-qrcode {
    text-align: center;

    img {
      width: 200px;
      height: 200px;
      border: 1px solid #f0f0f0;
    }

    .tip {
      margin-top: 15px;
      color: #606266;
      font-size: 14px;
    }
  }

  .loading-qrcode {
    padding: 60px 0;
    color: #909399;
    font-size: 14px;
    text-align: center;
  }

  .qrcode-error {
    padding: 48px 16px;
    text-align: center;

    p {
      margin: 0 0 8px;
      color: #64748b;
      font-size: 14px;
      line-height: 1.6;
    }
  }
}

/* 弹窗样式 */
.policy-dialog {
  .el-dialog {
    width: 60% !important;
    border-radius: 12px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    margin-top: 8vh !important;

    .el-dialog__header {
      padding: 16px 20px;
      margin: 0;
      border-bottom: 1px solid #e6e6e6;

      .dialog-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #2c3e50;
      }
    }

    .el-dialog__body {
      padding: 20px;
      flex: 1;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .el-dialog__footer {
      padding: 12px 20px;
      border-top: 1px solid #e6e6e6;
    }
  }
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  line-height: 1.6;
  color: #606266;
  padding-right: 10px;
  max-height: 60vh;

  h2 {
    color: #2c3e50;
    margin-top: 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
    font-size: 1.4em;
  }

  h3 {
    color: #34495e;
    margin-top: 18px;
    margin-bottom: 10px;
    font-size: 1.2em;
  }

  p, ul {
    margin-bottom: 12px;
    font-size: 14px;
  }

  ul {
    padding-left: 18px;

    li {
      margin-bottom: 6px;
    }
  }

  strong {
    color: #2c3e50;
  }
}

</style>
